import {
  useFeatureAppEnv,
  useFeatureFlags,
  usePriceType,
  useSalesChannel,
} from '@smart/feature-app/react';
import {ComponentProvider} from '@smart/react-components';
import {
  PageViewTrackContextProvider,
  QueryParamProvider,
} from '@smart/react-utils';
import type {MarketId} from '@smart/types';
import {TextButton} from '@smart/web-components';
import {Heading, Loader, Notification, Text} from '@smart/web-components';
import classNames from 'classnames';
import React, {useCallback} from 'react';
import type {AppModel} from '../../bff/app-model/create-app-model';
import type {ProductSearchProduct} from '../../bff/product-search-model/create-product-search-model';
import type {Banner as BannerType} from '../../bff/types';
import {Sorting} from '../../bff/types';
import {Select as SortBySelect} from '../components/sort-by-select';
import {UsedFeatureFlags} from '../constants/common';
import {
  AppModelProvider,
  ProductSearchProvider,
  useAppModel,
  useProductSearch,
} from '../contexts';
import {useDebouncedEffect} from '../hooks/use-debounce-effect';
import {useInfiniteScroll} from '../hooks/use-infinite-scroll';
import {useIsMobile} from '../hooks/use-is-mobile';
import {useLocale} from '../hooks/use-locale';
import {
  EventName,
  createFilterEvent,
  createPageView,
  useTracking,
} from '../hooks/use-tracking';
import {createProductCardProps} from '../utils/create-product-card-props';
import {getProductsAndFiltersForAnalytics} from '../utils/get-products-and-filters-for-analytics';
import {isStringEnum} from '../utils/is-string-enum';
import {AppLoader} from './app-loader';
import styles from './app.module.scss';
import Banner from './banner';
import type {FacetOption} from './facet-panel';
import {FacetPanel} from './facet-panel';
import {FacetPanelV2} from './facet-panel-v2';
import {FilterButtons} from './filter-buttons';
import {FilterNavigation} from './filter-navigation';
import {FilterTags} from './filter-tags';
import HeaderArea from './header-area';
import {ModelFilter} from './model-filter';
import {ModelFilterV2} from './model-filter-v2';
import {NoResult} from './no-result';
import {ProductCard} from './product-card';
export type AllProduct = BannerType | ProductSearchProduct;
function getFilterButtonText(
  activeFilters: FacetOption[] = [],
  appModel?: AppModel,
): string {
  if (!appModel) return ``;

  const showFilters = activeFilters.length > 0;

  return `${appModel.showFiltersButtonText} ${
    showFilters ? `(${activeFilters.length})` : ``
  }`;
}
function getFilterButtonTextV2(
  activeFilters: FacetOption[] = [],
  appModel?: AppModel,
  isFilterPanelVisible?: Boolean,
): string {
  if (!appModel) return ``;

  const showFilters = activeFilters.length > 0;

  return `${
    isFilterPanelVisible
      ? appModel.hideFiltersButtonText
      : appModel.showFiltersButtonText
  } ${showFilters ? `(${activeFilters.length})` : ``}`;
}

// eslint-disable-next-line complexity
function App(): JSX.Element {
  const {
    featureServices: {'payment-config-service': paymentConfigService},
  } = useFeatureAppEnv();
  const isMobile = useIsMobile();

  const [priceType] = usePriceType();

  const {marketId} = useLocale();
  const {enabled: enableFilters} = useFeatureFlags(
    UsedFeatureFlags.ENABLE_STOCK_SEARCH_FILTER_V2,
  );
  const track = useTracking();
  const {appModel, appMarketConfig} = useAppModel();
  const salesChannel = useSalesChannel();
  const {
    facets: filterState,
    activeFilters,
    sortBy,
    resetFilters,
    toggleFilterOption,
    setSortBy,
    products,
    newestProducts,
    loading: productSearchLoading,
    error: productSearchError,
    hasMore: hasMoreProducts,
    loadMore: loadMoreProducts,
  } = useProductSearch();
  const noResults = !productSearchLoading && products.length <= 0;
  const [isFilterPanelVisible, setIsFilterPanelVisible] =
    React.useState(enableFilters);
  const [pendingTrackingEvent, setPendingTrackingEvent] = React.useState<
    string | undefined
  >();
  const [sendPageViewEvent, setSendPageViewEvent] = React.useState(true);
  const {enabled: isHideSalesToolsLeasingRateEnabled} = useFeatureFlags(
    UsedFeatureFlags.HIDE_SALES_TOOLS_LEASING_RATE,
  );

  const {enabled: isEnergyLabelModalEnabled} = useFeatureFlags(
    UsedFeatureFlags.ENABLE_ENERGYLABEL_MODAL,
  );

  React.useEffect(() => {
    if (isMobile) setIsFilterPanelVisible(!isMobile);
  }, [isMobile]);

  const loaderRef = useInfiniteScroll(
    React.useCallback(() => {
      if (hasMoreProducts && !productSearchLoading) {
        loadMoreProducts();
        setPendingTrackingEvent(EventName.LAZY_LOAD);
      }
    }, [hasMoreProducts, productSearchLoading, loadMoreProducts]),
  );

  useDebouncedEffect(
    () => {
      if (productSearchLoading || isFilterPanelVisible || !pendingTrackingEvent)
        return;
      track.event(
        createFilterEvent({
          ...getProductsAndFiltersForAnalytics(
            filterState,
            newestProducts,
            products,
          ),
          name: pendingTrackingEvent as EventName,
        }),
      );

      setPendingTrackingEvent(undefined);
    },
    500, //wait for any async states to start
    [
      pendingTrackingEvent,
      productSearchLoading,
      isFilterPanelVisible,
      products,
      track,
      newestProducts,
      filterState,
    ],
  );

  useDebouncedEffect(
    () => {
      if (productSearchLoading || !sendPageViewEvent) return;
      track.pageView(
        createPageView({
          ...getProductsAndFiltersForAnalytics(filterState, products),
          url: window.location.href,
        }),
      );
      setSendPageViewEvent(false);
    },
    50,
    [track, filterState, products, sendPageViewEvent, productSearchLoading],
  );

  const handleTagRemoveClick = (option: FacetOption) => {
    toggleFilterOption({
      facetId: option.facetId,
      id: option.id,
      selected: false,
    });
    setPendingTrackingEvent(`remove.${option.facetId}`);
  };

  const handleResetFilterClick = () => {
    resetFilters();
    setPendingTrackingEvent(EventName.RESET);
  };

  const handleToggleFilterPanel = useCallback(() => {
    if (isFilterPanelVisible) {
      setPendingTrackingEvent(EventName.MANUAL);
    }
    setIsFilterPanelVisible(!isFilterPanelVisible);
  }, [isFilterPanelVisible]);

  const handleFirstFilterNavigationCta = () => {
    handleToggleFilterPanel();
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!isStringEnum(Sorting)(e.target.value)) {
      return console.error(`Unknown sorting value: ${e.target.value}`);
    }
    setSortBy(e.target.value as Sorting);
    setPendingTrackingEvent(EventName.SORT);
  };

  const handleModelClick = () => {
    setSendPageViewEvent(true);
  };
  const handleOutsideClick = React.useCallback(() => {
    if (!isMobile && enableFilters) return;
    isFilterPanelVisible && handleToggleFilterPanel();
  }, [isMobile, enableFilters, handleToggleFilterPanel, isFilterPanelVisible]);
  const productClassname = classNames(
    styles.productList,
    {
      [`${styles[`productList--no-results`]}`]: products.length <= 0,
    },
    {
      [`${styles[`productList--append`]}`]:
        enableFilters && !isFilterPanelVisible && !isMobile,
    },
  );

  if (!appModel || !appMarketConfig) {
    return (
      <div className={styles.app}>
        <Loader
          size="small"
          messageId={`loading-stock-search`}
          className={styles.appLoader}
        />
      </div>
    );
  }

  const filterButtonText = enableFilters
    ? getFilterButtonTextV2(activeFilters, appModel, isFilterPanelVisible)
    : getFilterButtonText(activeFilters, appModel);
  const filterButton = {
    children: filterButtonText,
    onClick: handleToggleFilterPanel,
  };
  return (
    <div className={styles.app}>
      {appModel.newHeadline ? (
        <HeaderArea
          newHeadline={appModel.newHeadline}
          subHeadline={appModel.subHeadline}
          uniqueSellingPoints={appModel.uniqueSellingPoints}
        />
      ) : (
        <Heading
          className={styles.appHeadline}
          variant={`headline-functional-200`}
        >
          {appModel.headline}
        </Heading>
      )}
      {enableFilters ? (
        <div className={styles.modelFilterContainer}>
          <ModelFilterV2
            onClick={handleModelClick}
            sortByButton={{
              label: appModel.sortByLabel,
              options: appModel.sortByOptions,
              value: sortBy,
              onChange: handleSortChange,
            }}
          />
        </div>
      ) : (
        <ModelFilter onClick={handleModelClick} />
      )}
      {enableFilters ? (
        <div className={styles.filterContainerV2}>
          <FilterButtons
            filterLength={activeFilters.length}
            filterButton={{
              children: filterButtonText,
              onClick: handleToggleFilterPanel,
            }}
            resetButton={{
              children: appModel.resetFiltersButtonText,
              onClick: handleResetFilterClick,
            }}
            sortByButton={{
              label: appModel.sortByLabel,
              options: appModel.sortByOptions,
              value: sortBy,
              onChange: handleSortChange,
            }}
          />
          <div className={styles.btnAndTagsContainer}>
            <TextButton
              {...filterButton}
              className={styles[`filter-button`]}
              variant={isMobile ? `secondary` : `ghost`}
              iconName="filter--small"
              size="200"
              iconPosition={`left`}
            />
            <SortBySelect
              className={styles.sortBySelectMobile}
              autoComplete="on"
              id="sort-by"
              name="sort-by"
              {...{
                label: appModel.sortByLabel,
                options: appModel.sortByOptions,
                value: sortBy,
                onChange: handleSortChange,
              }}
            />
            {activeFilters.length > 0 && (
              <div className={styles.filterTags}>
                {/* <Text variant={`paragraph-200`} className={styles.filterLabel}>
              {appModel.filtersAppliedText}
            </Text> */}
                <FilterTags
                  tags={activeFilters}
                  ariaLabel="Remove Tag"
                  handleRemove={handleTagRemoveClick}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className={styles.filterContainer}>
          <FilterButtons
            filterLength={activeFilters.length}
            filterButton={{
              children: filterButtonText,
              onClick: handleToggleFilterPanel,
            }}
            resetButton={{
              children: appModel.resetFiltersButtonText,
              onClick: handleResetFilterClick,
            }}
            sortByButton={{
              label: appModel.sortByLabel,
              options: appModel.sortByOptions,
              value: sortBy,
              onChange: handleSortChange,
            }}
          />
          {activeFilters.length > 0 && (
            <div className={styles.filterTags}>
              <Text variant={`paragraph-200`} className={styles.filterLabel}>
                {appModel.filtersAppliedText}
              </Text>
              <FilterTags
                tags={activeFilters}
                ariaLabel="Remove Tag"
                handleRemove={handleTagRemoveClick}
              />
            </div>
          )}
        </div>
      )}
      {productSearchError ? (
        <Notification
          className={styles.productListError}
          variant="error"
          text="Something went wrong!"
        />
      ) : null}
      {noResults ? (
        <NoResult
          heading={appModel.noProductFoundHeading}
          text={appModel.noProductFoundText}
          resetFiltersButtonLabel={appModel.resetAllFiltersButtonText}
          resetFiltersButtonHandler={handleResetFilterClick}
          productCatalogueButtonLabel={appModel.productCatalogueButtonText}
          productCatalogueButtonHref={appModel.productCatalogueButtonLink}
        />
      ) : (
        <div className={styles.productListAndPannelWrapper}>
          <FacetPanelV2
            show={enableFilters && isFilterPanelVisible}
            closeFacet={handleToggleFilterPanel}
          />
          <div className={styles.productsAndLoaderWrap}>
            <ul className={productClassname}>
              {products.map((item, index) => (
                <li key={`card-${index}`}>
                  {item.type === `banner` ? (
                    <Banner
                      {...item}
                      leasingPriceReference={appModel.leasingPriceReference}
                    />
                  ) : (
                    <ProductCard
                      {...createProductCardProps({
                        appMarketConfig,
                        marketId: marketId as MarketId,
                        paymentConfigService,
                        priceType,
                        appModel,
                        index,
                        item,
                        showLeasingTools: !isHideSalesToolsLeasingRateEnabled,
                        isEnergyLabelModalEnabled,
                        salesChannel,
                      })}
                    />
                  )}
                </li>
              ))}
            </ul>
            <div ref={loaderRef} className={styles.productListLoader}>
              {productSearchLoading && (
                <Loader size="medium" messageId={`loading-product-search`} />
              )}
            </div>
          </div>
        </div>
      )}

      <FilterNavigation
        ariaDescription={appModel.filterNavigationAriaDescriptionText}
        isOpen={
          enableFilters
            ? isMobile
              ? isFilterPanelVisible
              : false
            : isFilterPanelVisible
        }
        outsideClick={handleOutsideClick}
        firstCta={{
          children: appModel.showResultsButtonText,
          onClick: handleFirstFilterNavigationCta,
        }}
        secondCta={{
          children: appModel.resetAllFiltersButtonText,
          onClick: handleResetFilterClick,
        }}
      >
        <FacetPanel
          tagElement={
            <FilterTags
              tags={activeFilters}
              ariaLabel="Remove Tag"
              handleRemove={handleTagRemoveClick}
            />
          }
          closeFacet={handleToggleFilterPanel}
        />
      </FilterNavigation>
    </div>
  );
}

interface AppWrapperProps {
  readonly done: (error?: Error) => void;
}

export default ({done}: AppWrapperProps) => {
  const {
    featureServices: {'s2:history': historyService},
  } = useFeatureAppEnv();

  return (
    <QueryParamProvider>
      <PageViewTrackContextProvider historyService={historyService}>
        <AppModelProvider loader={<AppLoader />} done={done}>
          <ProductSearchProvider>
            <ComponentProvider theme="light">
              <App />
            </ComponentProvider>
          </ProductSearchProvider>
        </AppModelProvider>
      </PageViewTrackContextProvider>
    </QueryParamProvider>
  );
};
